import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  Form,
  Button,
  Space,
  Select,
  DatePicker,
  InputNumber,
  Input,
  Table,
  Row,
  Col,
  Typography,
  App,
  Spin,
  Tag,
  Alert,
  Divider,
  Checkbox,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  useReceiptVoucher,
  useCreateReceiptVoucher,
  useUpdateReceiptVoucher,
  usePostReceiptVoucher,
  useArSettlementContext,
} from '@/hooks/useReceiptVoucher';
import { useActiveCustomerOptions } from '@/hooks/useCustomers';
import { useChartAccountsByType } from '@/hooks/useSalesInvoice';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import InheritedAttachmentsCard from '@/components/documents/InheritedAttachmentsCard';
import { DOCUMENT_ENTITY_TYPE_RECEIPT_VOUCHER } from '@/constants/document-entity-types';
import { chartOfAccountsService } from '@/services/salesInvoice.service';
import salesInvoiceService from '@/services/salesInvoice.service';
import receiptVoucherService, {
  parseReceiptSettlementSnapshot,
  type ReceiptType,
  type ReceiptPaymentMode,
  type ReceiptSettlementLineInput,
  type CreateReceiptVoucherPayload,
  type ArSettlementContext,
} from '@/services/receiptVoucher.service';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useProjectsForSelection } from '@/hooks/useProject';

const { Title, Text } = Typography;
const { TextArea } = Input;

type SettlementRow = {
  sales_invoice_id: string;
  invoice_number: string;
  invoice_type?: string;
  due_date: string;
  grand_total: number;
  balance_due: number;
  days_overdue: number;
  payment_status?: string;
  cash_amount: number;
  selected: boolean;
};

const ReceiptVoucherFormPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const isNew = id === 'new' || !id;
  const isEdit = id && !isNew && !id.includes('new');

  const [form] = Form.useForm();
  const [settlementRows, setSettlementRows] = useState<SettlementRow[]>([]);
  const [advanceAdj, setAdvanceAdj] = useState<Record<string, number>>({});
  const [cnAdj, setCnAdj] = useState<Record<string, number>>({});
  const [allocations, setAllocations] = useState<
    { sales_invoice_id: string; invoice_number: string; outstanding: number; allocated_amount: number; credit_account_id?: string }[]
  >([]);
  const [directLines, setDirectLines] = useState<{ account_id: string; amount: number; description?: string }[]>([]);
  const [discountLines, setDiscountLines] = useState<
    { account_id: string; amount: number; description?: string }[]
  >([]);
  const [chargeLines, setChargeLines] = useState<
    { account_id: string; amount: number; description?: string; project_id?: string }[]
  >([]);

  const receiptTypes = useMemo(
    () =>
      [
        { value: 'AgainstInvoice' as ReceiptType, label: t('receiptTypeAgainstInvoice') },
        { value: 'DirectRevenue' as ReceiptType, label: t('receiptTypeDirectRevenue') },
        { value: 'AdvanceFromCustomer' as ReceiptType, label: t('receiptTypeAdvanceFromCustomer') },
        { value: 'OnAccount' as ReceiptType, label: t('receiptTypeOnAccount') },
        { value: 'Other' as ReceiptType, label: t('receiptTypeOther') },
      ],
    [t]
  );

  const paymentModes = useMemo(
    () =>
      [
        { value: 'Cash' as ReceiptPaymentMode, label: t('modeCash') },
        { value: 'Cheque' as ReceiptPaymentMode, label: t('modeCheque') },
        { value: 'BankTransfer' as ReceiptPaymentMode, label: t('modeBankTransfer') },
        { value: 'Card' as ReceiptPaymentMode, label: t('modeCard') },
        { value: 'Online' as ReceiptPaymentMode, label: t('modeOnline') },
        { value: 'Other' as ReceiptPaymentMode, label: t('modeOther') },
      ],
    [t]
  );

  const { baseCurrency, allCurrencies, loading: currenciesLoading } = useCurrency();
  const currencyOptions = useMemo(
    () =>
      allCurrencies.map((c) => ({
        value: c.currency_code,
        label: `${c.currency_code} - ${c.currency_name}`,
      })),
    [allCurrencies]
  );

  const { data: receipt, isLoading } = useReceiptVoucher(isEdit ? id : undefined);
  const createMutation = useCreateReceiptVoucher();
  const updateMutation = useUpdateReceiptVoucher();
  const postMutation = usePostReceiptVoucher();
  const { options: customerOptions = [] } = useActiveCustomerOptions();
  const { data: assetAccounts = [] } = useChartAccountsByType('Asset', false);
  const { data: allGlAccounts = [] } = useQuery({
    queryKey: ['chart-of-accounts', 'all-leaf-active'],
    queryFn: async () => {
      const rows = await chartOfAccountsService.getAll();
      return (Array.isArray(rows) ? rows : [])
        .filter((a: any) => !a?.is_group && a?.is_deleted !== true && a?.is_active !== false)
        .map((a: any) => ({
          id: String(a.id),
          account_code: String(a.account_code || ''),
          account_name: String(a.account_name || ''),
        }));
    },
  });
  const { data: projects = [] } = useProjectsForSelection();
  const projectOptions = useMemo(
    () =>
      (projects as any[]).map((p) => ({
        value: p.id,
        label: p.project_number ? `${p.project_number} - ${p.project_name}` : p.project_name ?? p.id,
      })),
    [projects]
  );

  const receiptType = Form.useWatch('receipt_type', form) as ReceiptType | undefined;
  const customerId = Form.useWatch('customer_id', form);
  const amount = Form.useWatch('amount', form);
  const currency = Form.useWatch('currency', form) as string | undefined;
  const writeOffAmountWatch = Form.useWatch('write_off_amount', form);

  const { data: arContext, isLoading: arContextLoading } = useArSettlementContext(
    receiptType === 'AgainstInvoice' ? customerId : undefined,
    receiptType === 'AgainstInvoice' ? currency : undefined
  );

  const accountOptions = useMemo(
    () =>
      allGlAccounts.map((a) => ({
        value: a.id,
        label: a.account_code ? `${a.account_code} - ${a.account_name}` : a.account_name,
      })),
    [allGlAccounts]
  );

  useEffect(() => {
    // Edit mode hydrates from settlement_snapshot / allocations — avoid wiping those rows.
    if (isEdit) return;
    if (receiptType === 'AgainstInvoice' && arContext?.invoices) {
      setSettlementRows(
        arContext.invoices.map((inv) => ({
          sales_invoice_id: inv.id,
          invoice_number: inv.invoice_number,
          invoice_type: inv.invoice_type,
          due_date: inv.due_date ?? '',
          grand_total: Number(inv.grand_total),
          balance_due: Number(inv.balance_due),
          days_overdue: Number(inv.days_overdue ?? 0),
          payment_status: inv.payment_status,
          cash_amount: 0,
          selected: false,
        }))
      );
      setAdvanceAdj({});
      setCnAdj({});
    } else if (receiptType !== 'AgainstInvoice') {
      setSettlementRows([]);
      setAdvanceAdj({});
      setCnAdj({});
    }
  }, [receiptType, arContext, isEdit]);

  useEffect(() => {
    if (isEdit) return;
    if (receiptType === 'AgainstInvoice' && currency) {
      setSettlementRows([]);
      setAdvanceAdj({});
      setCnAdj({});
    }
  }, [currency, receiptType, isEdit]);

  useEffect(() => {
    if (isEdit) return;
    if (!customerId && receiptType === 'AgainstInvoice') {
      setSettlementRows([]);
      setAdvanceAdj({});
      setCnAdj({});
    }
  }, [customerId, receiptType, isEdit]);

  useEffect(() => {
    if (receiptType === 'AdvanceFromCustomer' && customerId) {
      salesInvoiceService.getOutstandingByCustomer(customerId, 'Advance').then((invs) => {
        setAllocations(
          invs.map((inv) => ({
            sales_invoice_id: inv.id,
            invoice_number: inv.invoice_number ?? '',
            outstanding: Number(inv.balance_due ?? 0),
            allocated_amount: 0,
            credit_account_id: inv.advance_from_customer_account_id || undefined,
          }))
        );
      });
    } else if (receiptType !== 'AdvanceFromCustomer') {
      if (receiptType !== 'AgainstInvoice') {
        setAllocations([]);
      }
    }
    if (receiptType !== 'AgainstInvoice') {
      setDiscountLines([]);
      setChargeLines([]);
    }
  }, [receiptType, customerId]);

  useEffect(() => {
    if (receipt && isEdit) {
      form.setFieldsValue({
        receipt_date: receipt.receipt_date ? dayjs(receipt.receipt_date) : undefined,
        receipt_type: receipt.receipt_type,
        customer_id: receipt.customer_id,
        amount: receipt.amount,
        currency: receipt.currency ?? baseCurrency?.currency_code ?? 'AED',
        payment_mode: receipt.payment_mode,
        receiving_account_id: receipt.receiving_account_id,
        reference_number: receipt.reference_number,
        narration: receipt.narration,
        received_from_name: receipt.received_from_name,
        cheque_number: receipt.cheque_number,
        cheque_date: receipt.cheque_date ? dayjs(receipt.cheque_date) : undefined,
        bank_name: receipt.bank_name,
        transaction_reference: receipt.transaction_reference,
        department_id: receipt.department_id,
        write_off_amount: Number(receipt.write_off_amount ?? 0),
        write_off_account_id: receipt.write_off_account_id || undefined,
        settlement_remarks: receipt.settlement_remarks ?? undefined,
      });
      if (receipt.directLines?.length) {
        setDirectLines(
          receipt.directLines.map((l) => ({
            account_id: l.account_id,
            amount: Number(l.amount),
            description: l.description,
          }))
        );
      }
      if (receipt.discountLines?.length) {
        setDiscountLines(
          receipt.discountLines.map((l) => ({
            account_id: l.account_id,
            amount: Number(l.amount),
            description: l.description,
          }))
        );
      } else {
        setDiscountLines([]);
      }
      if (receipt.chargeLines?.length) {
        setChargeLines(
          receipt.chargeLines.map((l) => ({
            account_id: l.account_id,
            amount: Number(l.amount),
            description: l.description,
            project_id: l.project_id,
          }))
        );
      } else {
        setChargeLines([]);
      }

      const snapshotLines = parseReceiptSettlementSnapshot(receipt.settlement_snapshot);
      if (receipt.receipt_type === 'AgainstInvoice' && receipt.customer_id && snapshotLines.length) {
        receiptVoucherService
          .getArSettlementContext(receipt.customer_id, receipt.currency ?? 'AED')
          .then((ctx: ArSettlementContext) => {
            const byInv = new Map(ctx.invoices.map((i) => [i.id, i]));
            const hydratedIds = new Set(snapshotLines.map((l) => l.sales_invoice_id));
            const fromSnapshot: SettlementRow[] = snapshotLines.map((line) => {
              const inv = byInv.get(line.sales_invoice_id);
              return {
                sales_invoice_id: line.sales_invoice_id,
                invoice_number: inv?.invoice_number ?? line.sales_invoice_id,
                invoice_type: inv?.invoice_type,
                due_date: inv?.due_date ?? '',
                grand_total: Number(inv?.grand_total ?? 0),
                balance_due: Number(inv?.balance_due ?? 0) + Number(line.cash_amount ?? 0),
                days_overdue: Number(inv?.days_overdue ?? 0),
                payment_status: inv?.payment_status,
                cash_amount: Number(line.cash_amount ?? 0),
                selected:
                  Number(line.cash_amount) > 0 ||
                  (line.advance_adjustments ?? []).some((a) => Number(a.amount) > 0) ||
                  (line.credit_note_adjustments ?? []).some((c) => Number(c.amount) > 0),
              };
            });
            const extras = ctx.invoices
              .filter((inv) => !hydratedIds.has(inv.id))
              .map((inv) => ({
                sales_invoice_id: inv.id,
                invoice_number: inv.invoice_number,
                invoice_type: inv.invoice_type,
                due_date: inv.due_date ?? '',
                grand_total: Number(inv.grand_total),
                balance_due: Number(inv.balance_due),
                days_overdue: Number(inv.days_overdue ?? 0),
                payment_status: inv.payment_status,
                cash_amount: 0,
                selected: false,
              }));
            setSettlementRows([...fromSnapshot, ...extras]);
            const adv: Record<string, number> = {};
            const cn: Record<string, number> = {};
            for (const line of snapshotLines) {
              for (const a of line.advance_adjustments ?? []) {
                const advId = a.receipt_voucher_id || a.advance_sales_invoice_id;
                if (advId) adv[`${line.sales_invoice_id}:${advId}`] = Number(a.amount);
              }
              for (const c of line.credit_note_adjustments ?? []) {
                cn[`${line.sales_invoice_id}:${c.customer_credit_note_id}`] = Number(c.amount);
              }
            }
            setAdvanceAdj(adv);
            setCnAdj(cn);
          });
      } else if (receipt.receipt_type === 'AgainstInvoice' && receipt.customer_id && receipt.allocations?.length) {
        receiptVoucherService
          .getArSettlementContext(receipt.customer_id, receipt.currency ?? 'AED')
          .then((ctx: ArSettlementContext) => {
            const byAlloc = new Map(receipt.allocations!.map((a) => [a.sales_invoice_id, a]));
            setSettlementRows(
              ctx.invoices.map((inv) => {
                const alloc = byAlloc.get(inv.id);
                const cash = Number(alloc?.allocated_amount ?? 0);
                return {
                  sales_invoice_id: inv.id,
                  invoice_number: inv.invoice_number,
                  invoice_type: inv.invoice_type,
                  due_date: inv.due_date ?? '',
                  grand_total: Number(inv.grand_total),
                  balance_due: Number(inv.balance_due) + cash,
                  days_overdue: Number(inv.days_overdue ?? 0),
                  payment_status: inv.payment_status,
                  cash_amount: cash,
                  selected: cash > 0,
                };
              })
            );
          });
      } else if (receipt.receipt_type === 'AdvanceFromCustomer' && receipt.customer_id && receipt.allocations?.length) {
        salesInvoiceService.getOutstandingByCustomer(receipt.customer_id, 'Advance').then((invs) => {
          const byId = new Map(invs.map((i) => [i.id, i]));
          setAllocations(
            receipt.allocations!.map((a) => {
              const inv = byId.get(a.sales_invoice_id);
              return {
                sales_invoice_id: a.sales_invoice_id,
                invoice_number: inv?.invoice_number ?? '—',
                outstanding: inv ? Number(inv.balance_due ?? 0) : 0,
                allocated_amount: Number(a.allocated_amount),
                credit_account_id:
                  a.credit_account_id || inv?.advance_from_customer_account_id || undefined,
              };
            })
          );
        });
      } else if (receipt.allocations?.length) {
        setAllocations(
          receipt.allocations.map((a) => ({
            sales_invoice_id: a.sales_invoice_id,
            invoice_number: (a as any).invoice_number ?? '—',
            outstanding: (a as any).outstanding ?? 0,
            allocated_amount: Number(a.allocated_amount),
          }))
        );
      }
    }
  }, [receipt, isEdit, form, baseCurrency?.currency_code]);

  const rowAdvanceTotal = (invoiceId: string) =>
    (arContext?.advances ?? []).reduce(
      (s, adv) => s + Number(advanceAdj[`${invoiceId}:${adv.id}`] ?? 0),
      0
    );

  const rowCnTotal = (invoiceId: string) =>
    (arContext?.credit_notes ?? []).reduce(
      (s, cn) => s + Number(cnAdj[`${invoiceId}:${cn.id}`] ?? 0),
      0
    );

  const discountTotal = discountLines.reduce((s, l) => s + Number(l.amount || 0), 0);
  const chargesTotal = chargeLines.reduce((s, l) => s + Number(l.amount || 0), 0);
  const writeOffAmount = Number(writeOffAmountWatch ?? 0);
  const cashAmount = Number(amount || 0);

  const settlementSummary = useMemo(() => {
    const selected = settlementRows.filter(
      (r) =>
        r.selected ||
        r.cash_amount > 0 ||
        rowAdvanceTotal(r.sales_invoice_id) > 0 ||
        rowCnTotal(r.sales_invoice_id) > 0
    );
    const totalCashAlloc = selected.reduce((s, r) => s + r.cash_amount, 0);
    const totalAdv = Object.values(advanceAdj).reduce((s, v) => s + Number(v || 0), 0);
    const totalCn = Object.values(cnAdj).reduce((s, v) => s + Number(v || 0), 0);
    const covered = Math.round((cashAmount + discountTotal + writeOffAmount) * 100) / 100;
    const difference = Math.round((covered - totalCashAlloc) * 100) / 100;
    return {
      totalCashAlloc,
      totalAdv,
      totalCn,
      discountTotal,
      writeOffAmount,
      chargesTotal,
      cashAmount,
      covered,
      difference,
      invoiceCoverage: totalCashAlloc + totalAdv + totalCn,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- row helpers close over advanceAdj/cnAdj/arContext
  }, [
    settlementRows,
    advanceAdj,
    cnAdj,
    cashAmount,
    discountTotal,
    writeOffAmount,
    chargesTotal,
    arContext,
  ]);

  const buildSettlementLines = (): ReceiptSettlementLineInput[] =>
    settlementRows
      .filter(
        (r) =>
          r.selected ||
          r.cash_amount > 0 ||
          rowAdvanceTotal(r.sales_invoice_id) > 0 ||
          rowCnTotal(r.sales_invoice_id) > 0
      )
      .map((r) => {
        const advance_adjustments = (arContext?.advances ?? [])
          .map((adv) => {
            const amt = Number(advanceAdj[`${r.sales_invoice_id}:${adv.id}`] ?? 0);
            if (amt <= 0) return null;
            if (adv.source === 'advance_invoice') {
              return { advance_sales_invoice_id: adv.id, amount: amt };
            }
            return { receipt_voucher_id: adv.id, amount: amt };
          })
          .filter(Boolean) as ReceiptSettlementLineInput['advance_adjustments'];
        const credit_note_adjustments = (arContext?.credit_notes ?? [])
          .map((cn) => {
            const amt = Number(cnAdj[`${r.sales_invoice_id}:${cn.id}`] ?? 0);
            return amt > 0 ? { customer_credit_note_id: cn.id, amount: amt } : null;
          })
          .filter(Boolean) as ReceiptSettlementLineInput['credit_note_adjustments'];
        return {
          sales_invoice_id: r.sales_invoice_id,
          cash_amount: r.cash_amount,
          advance_adjustments,
          credit_note_adjustments,
        };
      });

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload: CreateReceiptVoucherPayload = {
        receipt_date: values.receipt_date ? dayjs(values.receipt_date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
        receipt_type: values.receipt_type,
        customer_id: values.customer_id,
        amount: Number(values.amount),
        currency: values.currency ?? 'AED',
        payment_mode: values.payment_mode,
        receiving_account_id: values.receiving_account_id,
        reference_number: values.reference_number,
        narration: values.narration,
        received_from_name: values.received_from_name,
        cheque_number: values.cheque_number,
        cheque_date: values.cheque_date ? dayjs(values.cheque_date).format('YYYY-MM-DD') : undefined,
        bank_name: values.bank_name,
        transaction_reference: values.transaction_reference,
        department_id: values.department_id,
      };

      if (values.receipt_type === 'AgainstInvoice') {
        const lines = buildSettlementLines();
        if (!lines.length) {
          message.error(t('errAddInvoiceSettlement'));
          return;
        }
        const totalCashAlloc = lines.reduce((s, l) => s + Number(l.cash_amount), 0);
        const discTotal = discountLines
          .filter((l) => l.amount > 0 && l.account_id)
          .reduce((s, l) => s + Number(l.amount), 0);
        const writeOff = Number(values.write_off_amount ?? 0);
        const cash = Number(values.amount);
        if (Math.abs(cash + discTotal + writeOff - totalCashAlloc) > 0.01) {
          message.error(
            t('errCashDiscountsWriteOffEqualAlloc', {
              cash: cash.toFixed(2),
              discounts: discTotal.toFixed(2),
              writeOff: writeOff.toFixed(2),
              allocations: totalCashAlloc.toFixed(2),
            })
          );
          return;
        }
        if (discountLines.some((l) => l.amount > 0 && !l.account_id)) {
          message.error(t('errFacilityDiscountNeedsAccount'));
          return;
        }
        if (writeOff > 0 && !values.write_off_account_id) {
          message.error(t('errWriteOffAccountRequired'));
          return;
        }
        const chargeLinesWithAmount = chargeLines.filter((l) => l.amount > 0 && l.account_id);
        if (chargeLines.some((l) => l.amount > 0 && !l.account_id)) {
          message.error(t('errChargeNeedsAccount'));
          return;
        }
        if (chargesTotal > cash + 0.01) {
          message.error(t('errBankChargesExceedCash'));
          return;
        }

        payload.settlement_lines = lines;
        payload.allocations = lines
          .filter((l) => l.cash_amount > 0)
          .map((l) => ({
            sales_invoice_id: l.sales_invoice_id,
            allocated_amount: l.cash_amount,
          }));
        payload.discount_lines = discountLines
          .filter((l) => l.amount > 0 && l.account_id)
          .map((l) => ({
            account_id: l.account_id,
            amount: Number(l.amount),
            description: l.description,
          }));
        payload.write_off_amount = writeOff;
        payload.write_off_account_id = writeOff > 0 ? values.write_off_account_id : undefined;
        payload.settlement_remarks = values.settlement_remarks;
        if (chargeLinesWithAmount.length) {
          payload.charge_lines = chargeLinesWithAmount.map((l) => ({
            account_id: l.account_id,
            amount: l.amount,
            description: l.description,
            project_id: l.project_id,
          }));
        } else {
          payload.charge_lines = [];
        }
      } else if (values.receipt_type === 'AdvanceFromCustomer' && allocations.some((a) => a.allocated_amount > 0)) {
        const totalAlloc = allocations.reduce((s, a) => s + a.allocated_amount, 0);
        if (Math.abs(totalAlloc - Number(values.amount)) > 0.01) {
          message.error(t('errTotalAllocationEqualsReceipt'));
          return;
        }
        payload.allocations = allocations
          .filter((a) => a.allocated_amount > 0)
          .map((a) => {
            const row: { sales_invoice_id: string; allocated_amount: number; credit_account_id?: string } = {
              sales_invoice_id: a.sales_invoice_id,
              allocated_amount: a.allocated_amount,
            };
            if (a.credit_account_id) {
              row.credit_account_id = a.credit_account_id;
            }
            return row;
          });
      } else {
        const linesWithAmount = directLines.filter((l) => l.amount > 0);
        if (linesWithAmount.length > 0) {
          const totalDirect = directLines.reduce((s, l) => s + l.amount, 0);
          if (Math.abs(totalDirect - Number(values.amount)) > 0.01) {
            message.error(t('errDirectLinesEqualReceipt'));
            return;
          }
        }
        payload.direct_lines = directLines
          .filter((l) => l.amount > 0)
          .map((l) => ({ account_id: l.account_id, amount: l.amount, description: l.description }));
      }

      if (isNew) {
        const created = await createMutation.mutateAsync(payload);
        if (created?.id) navigate(`/fin/receipt-vouchers/${created.id}`, { replace: true });
        else navigate('/fin/receipt-vouchers');
      } else if (id) {
        await updateMutation.mutateAsync({ id, payload });
        navigate(`/fin/receipt-vouchers/${id}`);
      }
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || t('errSaveFailed'));
    }
  };

  const handlePost = async () => {
    if (!id || id === 'new') return;
    try {
      await form.validateFields();
      if (receiptType === 'AgainstInvoice') {
        const lines = buildSettlementLines();
        const totalCashAlloc = lines.reduce((s, l) => s + Number(l.cash_amount), 0);
        const discTotal = discountLines
          .filter((l) => l.amount > 0 && l.account_id)
          .reduce((s, l) => s + Number(l.amount), 0);
        const writeOff = Number(form.getFieldValue('write_off_amount') ?? 0);
        const cash = Number(form.getFieldValue('amount'));
        if (Math.abs(cash + discTotal + writeOff - totalCashAlloc) > 0.01) {
          message.error(
            t('errCashDiscountsWriteOffEqualAlloc', {
              cash: cash.toFixed(2),
              discounts: discTotal.toFixed(2),
              writeOff: writeOff.toFixed(2),
              allocations: totalCashAlloc.toFixed(2),
            })
          );
          return;
        }
      } else if (receiptType === 'AdvanceFromCustomer' && allocations.some((a) => a.allocated_amount > 0)) {
        const totalAlloc = allocations.reduce((s, a) => s + a.allocated_amount, 0);
        if (Math.abs(totalAlloc - Number(form.getFieldValue('amount'))) > 0.01) {
          message.error(t('errTotalAllocationEqualsReceipt'));
          return;
        }
      } else {
        const linesWithAmount = directLines.filter((l) => l.amount > 0);
        if (linesWithAmount.length > 0) {
          const totalDirect = directLines.reduce((s, l) => s + l.amount, 0);
          if (Math.abs(totalDirect - Number(form.getFieldValue('amount'))) > 0.01) {
            message.error(t('errDirectLinesEqualReceipt'));
            return;
          }
        }
      }
      await postMutation.mutateAsync(id);
      navigate(`/fin/receipt-vouchers/${id}`);
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || t('errPostFailed'));
    }
  };

  const setCashAmount = (index: number, value: number) => {
    const row = settlementRows[index];
    const v = Math.min(Math.max(0, value), row.balance_due);
    const next = [...settlementRows];
    next[index] = { ...next[index], cash_amount: v, selected: v > 0 || next[index].selected };
    setSettlementRows(next);
  };

  const toggleRowSelected = (index: number, checked: boolean) => {
    const next = [...settlementRows];
    const row = next[index];
    next[index] = {
      ...row,
      selected: checked,
      cash_amount: checked ? (row.cash_amount > 0 ? row.cash_amount : row.balance_due) : 0,
    };
    setSettlementRows(next);
  };

  const allocateFullOutstanding = (index: number) => {
    setCashAmount(index, settlementRows[index].balance_due);
  };

  const setAllocation = (index: number, value: number) => {
    const out = allocations[index].outstanding;
    const v = Math.min(Math.max(0, value), out);
    const next = [...allocations];
    next[index] = { ...next[index], allocated_amount: v };
    setAllocations(next);
  };

  const setAllocationCreditAccount = (index: number, credit_account_id: string | undefined) => {
    const next = [...allocations];
    next[index] = { ...next[index], credit_account_id };
    setAllocations(next);
  };

  const addDirectLine = () => {
    setDirectLines([...directLines, { account_id: '', amount: 0 }]);
  };
  const updateDirectLine = (index: number, field: string, value: any) => {
    const next = [...directLines];
    (next[index] as any)[field] = value;
    setDirectLines(next);
  };
  const removeDirectLine = (index: number) => {
    setDirectLines(directLines.filter((_, i) => i !== index));
  };

  const addDiscountLine = () => {
    setDiscountLines([...discountLines, { account_id: '', amount: 0, description: t('facilityDiscountDefaultDesc') }]);
  };
  const updateDiscountLine = (index: number, field: string, value: unknown) => {
    const next = [...discountLines];
    (next[index] as any)[field] = value;
    setDiscountLines(next);
  };
  const removeDiscountLine = (index: number) => {
    setDiscountLines(discountLines.filter((_, i) => i !== index));
  };

  const addChargeLine = () => {
    setChargeLines([...chargeLines, { account_id: '', amount: 0, project_id: undefined }]);
  };
  const updateChargeLine = (index: number, field: string, value: unknown) => {
    const next = [...chargeLines];
    (next[index] as any)[field] = value;
    setChargeLines(next);
  };
  const removeChargeLine = (index: number) => {
    setChargeLines(chargeLines.filter((_, i) => i !== index));
  };

  if (isEdit && isLoading && !receipt) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  const isDraft = receipt?.status === 'Draft';
  const netBankInflow = Math.round((cashAmount - chargesTotal) * 100) / 100;

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/fin/receipt-vouchers')}>
            {tc('back')}
          </Button>
          <Title level={4} style={{ margin: '8px 0 0' }}>
            {isNew ? t('newReceiptVoucher') : t('receiptTitleWithNumber', { number: receipt?.receipt_number ?? id })}
          </Title>
        </Col>
        <Col>
          <Space>
            <Button icon={<SaveOutlined />} onClick={handleSave} loading={createMutation.isPending || updateMutation.isPending} disabled={!isDraft && !isNew}>
              {tc('save')}
            </Button>
            {isEdit && isDraft && (
              <Button type="primary" icon={<CheckCircleOutlined />} onClick={handlePost} loading={postMutation.isPending}>
                {tc('post')}
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          receipt_date: dayjs(),
          currency: baseCurrency?.currency_code ?? 'AED',
          receipt_type: 'AgainstInvoice',
          payment_mode: 'Cash',
          write_off_amount: 0,
        }}
      >
        <Card title={t('header')} style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="receipt_date" label={t('receiptDate')} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="department_id"
                label={t('department')}
                rules={[{ required: true, message: t('departmentRequired') }]}
              >
                <DepartmentSelect />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="receipt_type" label={t('receiptType')} rules={[{ required: true }]}>
                <Select options={receiptTypes} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="customer_id"
                label={t('customer')}
                rules={[
                  {
                    required:
                      receiptType === 'AgainstInvoice' ||
                      receiptType === 'AdvanceFromCustomer' ||
                      receiptType === 'OnAccount',
                  },
                ]}
              >
                <Select
                  allowClear
                  placeholder={t('selectCustomer')}
                  showSearch
                  optionFilterProp="label"
                  options={customerOptions}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="currency" label={t('currency')} rules={[{ required: true, message: t('selectCurrency') }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  loading={currenciesLoading}
                  options={currencyOptions}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="amount"
                label={receiptType === 'AgainstInvoice' ? t('cashReceived') : t('amount')}
                rules={[{ required: true }]}
              >
                <InputNumber min={0.01} style={{ width: '100%' }} addonAfter={currency || undefined} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="payment_mode" label={t('paymentMode')} rules={[{ required: true }]}>
                <Select options={paymentModes} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="receiving_account_id" label={t('receivingAccountCashBank')} rules={[{ required: true }]}>
                <Select
                  placeholder={t('selectAccount')}
                  showSearch
                  optionFilterProp="label"
                  options={assetAccounts.map((a) => ({ value: a.id, label: `${a.account_code} - ${a.account_name}` }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="reference_number" label={t('referenceNumber')}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="received_from_name" label={t('receivedFromName')}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="narration" label={t('narration')}>
                <TextArea rows={2} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="cheque_number" label={t('chequeNumber')}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="cheque_date" label={t('chequeDate')}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="bank_name" label={t('bankName')}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="transaction_reference" label={t('transactionReference')}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {receiptType === 'AgainstInvoice' && customerId && !currency && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('selectCurrencyBeforeTaxInvoices')}
          />
        )}

        {receiptType === 'AgainstInvoice' && customerId && currency && (
          <>
            <Card title={t('allocationAgainstTaxInvoices')} style={{ marginBottom: 16 }} loading={arContextLoading}>
              <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
                {t('allocationAgainstTaxHint', { currency })}
              </Typography.Paragraph>
              {!arContextLoading && !settlementRows.length && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message={t('noOpenInvoicesForCustomer', { currency })}
                />
              )}
              <Table
                size="small"
                rowKey="sales_invoice_id"
                dataSource={settlementRows}
                pagination={false}
                columns={[
                  {
                    title: '',
                    key: 'select',
                    width: 48,
                    render: (_: unknown, record: SettlementRow, index: number) => (
                      <Checkbox
                        checked={
                          record.selected ||
                          record.cash_amount > 0 ||
                          rowAdvanceTotal(record.sales_invoice_id) > 0 ||
                          rowCnTotal(record.sales_invoice_id) > 0
                        }
                        onChange={(e) => toggleRowSelected(index, e.target.checked)}
                      />
                    ),
                  },
                  {
                    title: t('invoice'),
                    dataIndex: 'invoice_number',
                    render: (text: string, record: SettlementRow) => (
                      <Space size={4}>
                        <span>{text}</span>
                        {record.invoice_type && record.invoice_type !== 'Standard' && (
                          <Tag color="blue">{record.invoice_type}</Tag>
                        )}
                      </Space>
                    ),
                  },
                  { title: t('due'), dataIndex: 'due_date', width: 100 },
                  {
                    title: t('age'),
                    dataIndex: 'days_overdue',
                    width: 72,
                    render: (d: number) =>
                      d > 0 ? <Tag color="red">{t('daysOverdue', { days: d })}</Tag> : <Tag>{t('current')}</Tag>,
                  },
                  {
                    title: t('outstanding'),
                    dataIndex: 'balance_due',
                    align: 'right',
                    render: (v: number) => `${currency ?? ''} ${Number(v).toFixed(2)}`.trim(),
                  },
                  {
                    title: t('cashAllocate'),
                    dataIndex: 'cash_amount',
                    align: 'right',
                    width: 150,
                    render: (v: number, record: SettlementRow, index: number) => (
                      <Space size="small">
                        <InputNumber
                          min={0}
                          max={record.balance_due}
                          value={v}
                          onChange={(val) => setCashAmount(index, Number(val) || 0)}
                          style={{ width: 100 }}
                        />
                        <Button type="link" size="small" onClick={() => allocateFullOutstanding(index)}>
                          {tc('full')}
                        </Button>
                      </Space>
                    ),
                  },
                  {
                    title: t('advance'),
                    key: 'advance',
                    align: 'right',
                    width: 90,
                    render: (_: unknown, record: SettlementRow) =>
                      rowAdvanceTotal(record.sales_invoice_id).toFixed(2),
                  },
                  {
                    title: t('creditNote'),
                    key: 'cn',
                    align: 'right',
                    width: 90,
                    render: (_: unknown, record: SettlementRow) =>
                      rowCnTotal(record.sales_invoice_id).toFixed(2),
                  },
                  {
                    title: t('lineTotal'),
                    key: 'line_total',
                    align: 'right',
                    width: 100,
                    render: (_: unknown, record: SettlementRow) =>
                      (
                        record.cash_amount +
                        rowAdvanceTotal(record.sales_invoice_id) +
                        rowCnTotal(record.sales_invoice_id)
                      ).toFixed(2),
                  },
                ]}
              />
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                {t('totalCashAllocatedAndCoverage', {
                  cash: settlementSummary.totalCashAlloc.toFixed(2),
                  coverage: settlementSummary.invoiceCoverage.toFixed(2),
                })}
              </Text>
            </Card>

            {!!arContext?.advances?.length && (
              <Card title={t('advanceAdjustments')} style={{ marginBottom: 16 }}>
                {arContext.advances.map((adv) => (
                  <div key={adv.id} style={{ marginBottom: 12 }}>
                    <Text strong>{adv.receipt_number}</Text>
                    <Text type="secondary">
                      {' '}
                      {t('advanceSourceOpen', {
                        source: adv.source === 'advance_invoice' ? t('advanceInvoice') : t('advanceReceipt'),
                        amount: Number(adv.open_balance).toFixed(2),
                      })}
                    </Text>
                    {settlementRows.map((row) => (
                      <Row key={`${row.sales_invoice_id}-${adv.id}`} gutter={8} align="middle" style={{ marginTop: 8 }}>
                        <Col span={10}>{row.invoice_number}</Col>
                        <Col span={8}>
                          <InputNumber
                            min={0}
                            max={adv.open_balance}
                            style={{ width: '100%' }}
                            value={advanceAdj[`${row.sales_invoice_id}:${adv.id}`] ?? 0}
                            onChange={(val) =>
                              setAdvanceAdj((prev) => ({
                                ...prev,
                                [`${row.sales_invoice_id}:${adv.id}`]: Number(val) || 0,
                              }))
                            }
                          />
                        </Col>
                      </Row>
                    ))}
                  </div>
                ))}
              </Card>
            )}

            {!!arContext?.credit_notes?.length && (
              <Card title={t('creditNoteAdjustments')} style={{ marginBottom: 16 }}>
                {arContext.credit_notes.map((cn) => (
                  <div key={cn.id} style={{ marginBottom: 12 }}>
                    <Text strong>{cn.credit_note_number}</Text>
                    <Text type="secondary"> {t('creditNoteOpen', { amount: Number(cn.open_balance).toFixed(2) })}</Text>
                    {settlementRows.map((row) => (
                      <Row key={`${row.sales_invoice_id}-${cn.id}`} gutter={8} align="middle" style={{ marginTop: 8 }}>
                        <Col span={10}>{row.invoice_number}</Col>
                        <Col span={8}>
                          <InputNumber
                            min={0}
                            max={cn.open_balance}
                            style={{ width: '100%' }}
                            value={cnAdj[`${row.sales_invoice_id}:${cn.id}`] ?? 0}
                            onChange={(val) =>
                              setCnAdj((prev) => ({
                                ...prev,
                                [`${row.sales_invoice_id}:${cn.id}`]: Number(val) || 0,
                              }))
                            }
                          />
                        </Col>
                      </Row>
                    ))}
                  </div>
                ))}
              </Card>
            )}

            <Card title={t('additionalDiscountFacility')} style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                {t('facilityDiscountHint')}
              </Text>
              <Button type="dashed" onClick={addDiscountLine} style={{ marginBottom: 8 }}>
                {t('addDiscountLine')}
              </Button>
              <Table
                size="small"
                rowKey={(_, i) => `disc-${i}`}
                dataSource={discountLines}
                pagination={false}
                columns={[
                  {
                    title: t('account'),
                    dataIndex: 'account_id',
                    render: (_: unknown, record: (typeof discountLines)[0], index: number) => (
                      <Select
                        style={{ width: 280 }}
                        placeholder={t('discountInterestExpense')}
                        showSearch
                        optionFilterProp="label"
                        value={record.account_id || undefined}
                        onChange={(v) => updateDiscountLine(index, 'account_id', v)}
                        options={accountOptions}
                      />
                    ),
                  },
                  {
                    title: t('amount'),
                    dataIndex: 'amount',
                    render: (_: unknown, record: (typeof discountLines)[0], index: number) => (
                      <InputNumber
                        min={0}
                        value={record.amount}
                        onChange={(v) => updateDiscountLine(index, 'amount', Number(v) || 0)}
                        style={{ width: 140 }}
                      />
                    ),
                  },
                  {
                    title: t('description'),
                    dataIndex: 'description',
                    render: (_: unknown, record: (typeof discountLines)[0], index: number) => (
                      <Input
                        value={record.description}
                        onChange={(e) => updateDiscountLine(index, 'description', e.target.value)}
                      />
                    ),
                  },
                  {
                    title: '',
                    render: (_: unknown, __: unknown, index: number) => (
                      <Button type="link" danger size="small" onClick={() => removeDiscountLine(index)}>
                        {tc('remove')}
                      </Button>
                    ),
                  },
                ]}
              />
            </Card>

            <Card title={t('writeOffAndSettlementRemarks')} style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="write_off_amount" label={t('writeOff')}>
                    <InputNumber min={0} style={{ width: '100%' }} addonAfter={currency || undefined} />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Form.Item
                    name="write_off_account_id"
                    label={t('writeOffAccount')}
                    rules={[
                      {
                        validator: async (_, value) => {
                          const wo = Number(form.getFieldValue('write_off_amount') ?? 0);
                          if (wo > 0 && !value) {
                            throw new Error(t('writeOffAccountRequiredWhenPositive'));
                          }
                        },
                      },
                    ]}
                  >
                    <Select
                      allowClear
                      placeholder={t('selectWriteOffAccount')}
                      showSearch
                      optionFilterProp="label"
                      options={accountOptions}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="settlement_remarks" label={t('settlementRemarks')}>
                    <TextArea rows={2} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card title={t('additionalCharges')} style={{ marginBottom: 16 }}>
              <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
                {t('rvAdditionalChargesHint')}
              </Typography.Paragraph>
              <Button type="dashed" onClick={addChargeLine} style={{ marginBottom: 8 }}>
                {t('addChargeLine')}
              </Button>
              <Table
                size="small"
                rowKey={(_, i) => `charge-${i}`}
                dataSource={chargeLines}
                pagination={false}
                columns={[
                  {
                    title: t('account'),
                    dataIndex: 'account_id',
                    render: (_: unknown, record: (typeof chargeLines)[0], index: number) => (
                      <Select
                        style={{ width: 280 }}
                        placeholder={t('selectAccount')}
                        showSearch
                        optionFilterProp="label"
                        value={record.account_id || undefined}
                        onChange={(v) => updateChargeLine(index, 'account_id', v)}
                        options={accountOptions}
                      />
                    ),
                  },
                  {
                    title: t('project'),
                    dataIndex: 'project_id',
                    render: (_: unknown, record: (typeof chargeLines)[0], index: number) => (
                      <Select
                        style={{ width: 220 }}
                        placeholder={t('optional')}
                        showSearch
                        allowClear
                        optionFilterProp="label"
                        value={record.project_id || undefined}
                        onChange={(v) => updateChargeLine(index, 'project_id', v)}
                        options={projectOptions}
                      />
                    ),
                  },
                  {
                    title: t('amount'),
                    dataIndex: 'amount',
                    render: (_: unknown, record: (typeof chargeLines)[0], index: number) => (
                      <InputNumber
                        min={0}
                        value={record.amount}
                        onChange={(v) => updateChargeLine(index, 'amount', Number(v) || 0)}
                        style={{ width: 120 }}
                        addonAfter={currency || undefined}
                      />
                    ),
                  },
                  {
                    title: t('description'),
                    dataIndex: 'description',
                    render: (_: unknown, record: (typeof chargeLines)[0], index: number) => (
                      <Input
                        value={record.description}
                        onChange={(e) => updateChargeLine(index, 'description', e.target.value)}
                      />
                    ),
                  },
                  {
                    title: '',
                    render: (_: unknown, __: unknown, index: number) => (
                      <Button type="link" danger size="small" onClick={() => removeChargeLine(index)}>
                        {tc('remove')}
                      </Button>
                    ),
                  },
                ]}
              />
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                {t('cashChargesNetBankInflow', {
                  currency: currency ?? '',
                  cash: cashAmount.toFixed(2),
                  charges: chargesTotal.toFixed(2),
                  net: netBankInflow.toFixed(2),
                })}
              </Text>
            </Card>

            <Card title={t('settlementSummary')} style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Text>{t('cashReceivedLine', { amount: settlementSummary.cashAmount.toFixed(2) })}</Text>
                  <br />
                  <Text>{t('plusFacilityDiscounts', { amount: settlementSummary.discountTotal.toFixed(2) })}</Text>
                  <br />
                  <Text>{t('plusWriteOff', { amount: settlementSummary.writeOffAmount.toFixed(2) })}</Text>
                  <br />
                  <Text>{t('equalsCovered', { amount: settlementSummary.covered.toFixed(2) })}</Text>
                  <br />
                  <Text>{t('cashAllocationsLine', { amount: settlementSummary.totalCashAlloc.toFixed(2) })}</Text>
                  <br />
                  <Text>{t('advancesApplied', { amount: settlementSummary.totalAdv.toFixed(2) })}</Text>
                  <br />
                  <Text>{t('creditNotesApplied', { amount: settlementSummary.totalCn.toFixed(2) })}</Text>
                  <br />
                  <Text>{t('chargesBank', { amount: settlementSummary.chargesTotal.toFixed(2) })}</Text>
                </Col>
                <Col span={12}>
                  <Alert
                    type={Math.abs(settlementSummary.difference) > 0.01 ? 'error' : 'success'}
                    message={
                      Math.abs(settlementSummary.difference) > 0.01
                        ? t('differenceMustBeZero', { amount: settlementSummary.difference.toFixed(2) })
                        : t('settlementBalanced')
                    }
                    description={t('settlementBalanceRules')}
                  />
                </Col>
              </Row>
              <Divider />
              <Text type="secondary">
                {t('invoiceCoverageCashAdvCn', { amount: settlementSummary.invoiceCoverage.toFixed(2) })}
              </Text>
            </Card>
          </>
        )}

        {receiptType === 'AdvanceFromCustomer' && customerId && (
          <Card title={t('allocationAgainstAdvanceInvoices')} style={{ marginBottom: 16 }}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              {t('advanceCreditAccountHint')}
            </Text>
            <Table
              size="small"
              rowKey="sales_invoice_id"
              dataSource={allocations}
              pagination={false}
              columns={[
                { title: t('invoice'), dataIndex: 'invoice_number', key: 'invoice_number' },
                {
                  title: t('creditAccountGl'),
                  key: 'credit_account_id',
                  width: 320,
                  render: (_: unknown, _record: (typeof allocations)[0], index: number) => (
                    <Select
                      style={{ width: '100%' }}
                      allowClear
                      placeholder={t('fromInvoiceDefault')}
                      showSearch
                      optionFilterProp="label"
                      value={allocations[index]?.credit_account_id || undefined}
                      onChange={(v) => setAllocationCreditAccount(index, v)}
                      options={accountOptions}
                    />
                  ),
                },
                { title: t('outstanding'), dataIndex: 'outstanding', key: 'outstanding', align: 'right', render: (v: number) => Number(v).toFixed(2) },
                {
                  title: t('allocate'),
                  dataIndex: 'allocated_amount',
                  key: 'allocated_amount',
                  align: 'right',
                  render: (v: number, record: any, index: number) => (
                    <InputNumber
                      min={0}
                      max={record.outstanding}
                      value={v}
                      onChange={(val) => setAllocation(index, Number(val) || 0)}
                      style={{ width: 120 }}
                    />
                  ),
                },
              ]}
            />
            <Text type="secondary">
              {t('totalAllocatedAmount', {
                amount: allocations.reduce((s, a) => s + a.allocated_amount, 0).toFixed(2),
              })}
            </Text>
          </Card>
        )}

        {(receiptType === 'DirectRevenue' || receiptType === 'AdvanceFromCustomer' || receiptType === 'Other') && (
          <Card title={t('directLines')} style={{ marginBottom: 16 }}>
            <Button type="dashed" onClick={addDirectLine} style={{ marginBottom: 8 }}>
              {t('addLine')}
            </Button>
            <Table
              size="small"
              rowKey={(_, i) => String(i)}
              dataSource={directLines}
              pagination={false}
              columns={[
                {
                  title: t('account'),
                  dataIndex: 'account_id',
                  render: (_, record, index) => (
                    <Select
                      style={{ width: 280 }}
                      placeholder={t('selectAccount')}
                      showSearch
                      optionFilterProp="label"
                      value={record.account_id || undefined}
                      onChange={(v) => updateDirectLine(index, 'account_id', v)}
                      options={accountOptions}
                    />
                  ),
                },
                {
                  title: t('amount'),
                  dataIndex: 'amount',
                  render: (_, record, index) => (
                    <InputNumber
                      min={0}
                      value={record.amount}
                      onChange={(v) => updateDirectLine(index, 'amount', Number(v) || 0)}
                      style={{ width: 120 }}
                    />
                  ),
                },
                {
                  title: t('description'),
                  dataIndex: 'description',
                  render: (_, record, index) => (
                    <Input value={record.description} onChange={(e) => updateDirectLine(index, 'description', e.target.value)} />
                  ),
                },
                {
                  title: '',
                  render: (_, __, index) => (
                    <Button type="link" danger size="small" onClick={() => removeDirectLine(index)}>
                      {tc('remove')}
                    </Button>
                  ),
                },
              ]}
            />
            <Text type="secondary">
              {t('totalAmountLine', { amount: directLines.reduce((s, l) => s + l.amount, 0).toFixed(2) })}
            </Text>
          </Card>
        )}
      </Form>
      {isEdit && id ? (
        <div style={{ marginTop: 16 }}>
          <EntityAttachmentsCard
            entityType={DOCUMENT_ENTITY_TYPE_RECEIPT_VOUCHER}
            entityId={id}
            title={tc('attachments')}
            readOnly={receipt?.status === 'Cancelled'}
            queryKeyPrefix={['documents', 'receipt-voucher']}
            uploadHint={t('attachmentsUploadHint')}
          />
          <InheritedAttachmentsCard
            trailDomain="sales"
            entityType={DOCUMENT_ENTITY_TYPE_RECEIPT_VOUCHER}
            entityId={id}
            queryKeyPrefix={['documents', 'receipt-voucher']}
          />
        </div>
      ) : null}
    </div>
  );
};

export default ReceiptVoucherFormPage;
